import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Pause, Play, Repeat, Zap } from "lucide-react";
import { recurringApi, type RecurringKind, type Recurring, type RecurringMade, type RecurringRow } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { fmtAmount } from "../lib/money";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtDate } from "../companies/CompanyPicker";
import { toUrl } from "../shell/routes";
import { everyText, VoucherPage } from "../vouchers/VoucherEntry";

// Recurring invoices and bills (docs/app.md §22): patterns that make an entry on each date of a schedule. Nothing runs by
// itself: what is due shows here and on Today, and "Make what is due" makes it (drafts, or posted when the pattern says so).

const K = {
  sales: { section: "Sales", many: "Recurring invoices", one: "recurring invoice", doc: "invoice", docs: "invoices", newCode: "RI01", listCode: "RI10" },
  purchase: { section: "Purchases", many: "Recurring bills", one: "recurring bill", doc: "bill", docs: "bills", newCode: "RB01", listCode: "RB10" },
} as const;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const base = (kind: RecurringKind) => `recurring/${kind}`;
const STATUS: Record<RecurringRow["status"], [string, string]> = { active: ["Active", "state-ready"], paused: ["Paused", "state-upcoming"], ended: ["Ended", "state-upcoming"] };

function Pill({ r, today }: { r: RecurringRow; today: string }) {
  if (r.status === "active" && r.last_problem) return <span className="pill state-overdue">Stuck</span>;
  if (r.status === "active" && r.next_on && r.next_on <= today) return <span className="pill state-due">Due</span>;
  const [t, c] = STATUS[r.status];
  return <span className={`pill ${c}`}>{t}</span>;
}

function madeText(m: RecurringMade[], doc: string, docs: string) {
  const made = m.filter((x) => x.voucher_id), stuck = m.filter((x) => x.problem);
  const posted = made.filter((x) => x.posted).length;
  const parts = [made.length ? `Made ${made.length} ${made.length === 1 ? doc : docs}${posted ? ` (${posted} posted${posted < made.length ? `, ${made.length - posted} as draft${made.length - posted === 1 ? "" : "s"}` : ""})` : made.length === 1 ? " as a draft" : " as drafts"}.` : "Nothing was due."];
  for (const x of stuck) parts.push(`${x.name}: ${x.problem}`);
  for (const x of made.filter((y) => y.not_posted)) parts.push(`${x.number} left as a draft: ${x.not_posted}`);
  return parts.join(" ");
}

// ---------- the list ----------
export function RecurringList({ kind }: { kind: RecurringKind }) {
  const { company } = useCompany();
  const nav = useNavigate();
  const k = K[kind];
  const today = iso(new Date());
  const { data, error, loading, reload } = useLoad(() => recurringApi.list(company.id, kind), [company.id, kind]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const rows = data ?? [];
  const due = useMemo(() => rows.filter((r) => r.status === "active" && r.next_on && r.next_on <= today), [rows, today]);
  const runDue = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const out: RecurringMade[] = [];
      for (const r of due) out.push(...(await recurringApi.run(company.id, r.id, today)));
      setMsg(madeText(out, k.doc, k.docs));
      await reload();
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  return (
    <section className="page">
      <PageHead crumb={k.section} title={k.many} code={k.listCode}>
        <button className="primary" onClick={() => nav(toUrl(`${base(kind)}/new`))}>New {k.one} <span className="code">{k.newCode}</span></button>
      </PageHead>
      {due.length > 0 && (
        <div className="due-strip">
          <Zap size={18} aria-hidden="true" />
          <span><b>{due.length} due</b> · {due.map((r) => r.name).slice(0, 4).join(", ")}{due.length > 4 ? "…" : ""}</span>
          <button className="primary" disabled={busy} onClick={() => void runDue()}>{busy ? "Making…" : "Make what is due"}</button>
        </div>
      )}
      {msg && <p className="msg ok" role="status">{msg}</p>}
      <ErrorNote text={err ?? error?.message} />
      {loading && !data ? <Loading /> : rows.length === 0 ? (
        <div className="card empty-card">
          <Repeat size={22} aria-hidden="true" />
          <p>Nothing repeats yet. {k.newCode} sets one up, or open a posted {k.doc} and choose <b>Repeat</b>.</p>
        </div>
      ) : (
        <table className="grid-table">
          <thead><tr><th>Name</th><th>Party</th><th>Repeats</th><th>Next</th><th>On its date</th><th>Status</th><th className="num">Amount</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className={r.status !== "active" ? "dim" : ""} onClick={() => nav(toUrl(`${base(kind)}/${r.id}`))}>
              <td><Link to={toUrl(`${base(kind)}/${r.id}`)} onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                {r.last_problem && <div className="small err-text">{r.last_problem}</div>}</td>
              <td>{r.party ?? ""}</td>
              <td>{everyText(r.frequency, r.every)}{r.ends_on ? <span className="muted small"> until {fmtDate(r.ends_on)}</span> : ""}</td>
              <td>{r.next_on ? fmtDate(r.next_on) : <span className="muted">—</span>}</td>
              <td className="muted">{r.auto_post ? "Posted" : "Draft"}</td>
              <td><Pill r={r} today={today} /></td>
              <td className="num">{fmtAmount(r.amount)}</td>
            </tr>))}</tbody>
        </table>
      )}
      <p className="muted small">Amounts are before tax. Due ones also show on Today (HM01).</p>
    </section>
  );
}

// ---------- one pattern ----------
export function RecurringPage({ kind }: { kind: RecurringKind }) {
  const { company } = useCompany();
  const { recurringId } = useParams();
  const isNew = !recurringId || recurringId === "new";
  const [r, setR] = useState<Recurring | null>(null);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const k = K[kind];
  const today = iso(new Date());
  const load = () => recurringApi.get(company.id, recurringId!).then(setR).catch((e) => setErr(errText(e)));
  useEffect(() => { setR(null); setEditing(false); setMsg(null); if (!isNew) void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [company.id, recurringId]);

  if (isNew) return <VoucherPage base={kind} recurring />;
  if (err && !r) return <section className="page"><PageHead crumb={k.section} title={k.many} /><ErrorNote text={err} /></section>;
  if (!r) return <section className="page"><PageHead crumb={k.section} title={k.many} /><Loading /></section>;
  if (editing) return <VoucherPage base={kind} recurring={r} onDone={() => { setEditing(false); void load(); }} />;

  const run = async (fn: () => Promise<void>) => { setErr(null); setMsg(null); setBusy(true); try { await fn(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); } };
  const makeDue = () => run(async () => { const m = await recurringApi.run(company.id, r.id, today); setMsg(madeText(m, k.doc, k.docs)); await load(); });
  const status = (s: "active" | "paused" | "ended") => run(async () => setR(await recurringApi.setStatus(company.id, r.id, s)));
  const due = r.status === "active" && !!r.next_on && r.next_on <= today;

  return (
    <section className="page">
      <PageHead crumb={`${k.section} › ${k.many}`} title={r.name}><Pill r={r} today={today} /></PageHead>
      <p className="muted">{r.party ? `${r.party} · ` : ""}{everyText(r.frequency, r.every)} from {fmtDate(r.starts_on)}{r.ends_on ? ` until ${fmtDate(r.ends_on)}` : ""}
        {" · "}{r.auto_post ? "posted on its date" : "made as a draft on its date"} · {fmtAmount(r.amount)} before tax</p>
      <div className="actions order-actions">
        {due && r.can.edit && <button className="primary" disabled={busy} onClick={() => void makeDue()}><Zap size={16} aria-hidden="true" /> Make what is due</button>}
        {r.can.edit && <button disabled={busy} onClick={() => setEditing(true)}>Change</button>}
        {r.can.edit && r.status === "active" && <button disabled={busy} onClick={() => void status("paused")}><Pause size={15} aria-hidden="true" /> Pause</button>}
        {r.can.edit && r.status !== "active" && <button disabled={busy} onClick={() => void status("active")}><Play size={15} aria-hidden="true" /> {r.status === "ended" ? "Start again" : "Resume"}</button>}
        {r.can.edit && r.status !== "ended" && <button className="link danger-link" disabled={busy} onClick={() => void status("ended")}>End it</button>}
      </div>
      {r.last_problem && r.status === "active" && <p className="msg err" role="alert">Stuck on {r.last_problem}. Put that right (or change the pattern), then make what is due again.</p>}
      {msg && <p className="msg ok" role="status">{msg}</p>}
      <ErrorNote text={err} />

      <div className="today-cols">
        <section className="card today-box grow2">
          <header><h2>Coming up</h2></header>
          {!r.upcoming?.length ? <p className="muted pad">{r.status === "paused" ? "Paused: nothing is made until it is resumed." : "Nothing more: it has ended."}</p> :
            <table className="grid-table"><tbody>{r.upcoming.map((d) => (
              <tr key={d}><td>{fmtDate(d)}</td><td className="muted small">{d <= today ? "due" : ""}</td></tr>))}</tbody></table>}
        </section>
        <section className="card today-box grow3">
          <header><h2>{k.docs[0].toUpperCase() + k.docs.slice(1)} made from it</h2><span className="muted small">{r.made} so far</span></header>
          {r.runs.length === 0 ? <p className="muted pad">None yet.</p> :
            <table className="grid-table"><tbody>{r.runs.map((x) => (
              <tr key={x.date}><td>{fmtDate(x.date)}</td><td><Link to={toUrl(`vouchers/v/${x.id}`)}>{x.number ?? "draft"}</Link>
                {x.status !== "posted" && <span className={`tag ${x.status}`}>{x.status}</span>}</td><td className="num">{fmtAmount(x.amount)}</td></tr>))}</tbody></table>}
        </section>
      </div>
    </section>
  );
}

// a box for the Sales / Purchases home: what repeats and what is due
export function RecurringBox({ kind }: { kind: RecurringKind }) {
  const { company } = useCompany();
  const { data } = useLoad(() => recurringApi.list(company.id, kind).catch(() => [] as RecurringRow[]), [company.id, kind]);
  const k = K[kind];
  const today = iso(new Date());
  const rows = (data ?? []).filter((r) => r.status === "active");
  if (!rows.length) return null;
  return (
    <section className="card today-box grow2">
      <header><h2>Repeating</h2><Link className="small" to={toUrl(base(kind))}>{k.many} <span className="code">{k.listCode}</span></Link></header>
      <table className="grid-table"><tbody>{rows.slice(0, 5).map((r) => (
        <tr key={r.id}><td><Link to={toUrl(`${base(kind)}/${r.id}`)}>{r.name}</Link></td><td className="muted small">{everyText(r.frequency, r.every)}</td>
          <td>{r.next_on ? fmtDate(r.next_on) : ""}</td><td><Pill r={r} today={today} /></td></tr>))}</tbody></table>
    </section>
  );
}
